'use client';

import { nextOccurrence } from "@/app/features/sites/opening-hours/helper/next-occurrence";
import { DateTime } from "luxon";
import { alpha, Box, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Enum_Specialopeninghour_Type, SpecialOpeningHour } from "@/app/lib/generated/graphql-types";
import { useMemo } from "react";
import { RRule } from "rrule";
import useIsDesktop from "@/hooks/useDesktop";
import VisiblyScrollableV2 from "@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable";

interface SpecialOpeningHoursViewProps {
    specialOpeningHours: SpecialOpeningHour[];
}

export default function SpecialOpeningHoursView({ specialOpeningHours }: SpecialOpeningHoursViewProps) {
    const groupedFutureEvents = specialOpeningHours
        .filter((event) =>
            nextOccurrence(event) !== null
        )
        .sort(sortByNextOccurrence)
        .reduce(groupedByMonthAndYear, {})

    const monthYearGroupName = (monthYearKey: string) => {

        const date = DateTime.fromJSDate(nextOccurrence(groupedFutureEvents[monthYearKey][0]!)!)

        const month = date.toFormat("MMMM")
        const year = date.toFormat("yy")

        return `${month} '${year}`
    }

    return (
        <VisiblyScrollableV2
            sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                maxHeight: '100%',
            }}
        >
            <Stack
                className="special-opening-hours-list w-full"
                spacing={0}
            >
                {Object.keys(groupedFutureEvents).map((monthYearKey) =>
                    <Box key={monthYearKey}>
                        <Typography
                            variant="overline"
                            sx={{
                                pt: 2,
                                pb: 1,
                                textTransform: 'uppercase',
                                fontWeight: 900,
                                color: (theme) => alpha(theme.palette.text.primary, .75)
                            }}
                        >
                            {monthYearGroupName(monthYearKey)}
                        </Typography>
                        {groupedFutureEvents[monthYearKey].map((specialOpeningHour, index) =>
                            <ListItem key={index} data={specialOpeningHour} />
                        )}
                    </Box>
                )}
            </Stack>
        </VisiblyScrollableV2>
    )
}

interface ListItemProps {
    data: SpecialOpeningHour;
}

function ListItem({ data }: ListItemProps) {
    const isDesktop = useIsDesktop();

    const typeTextMap = {
        [Enum_Specialopeninghour_Type.Open]: "Geöffnet",
        [Enum_Specialopeninghour_Type.Closed]: "Geschlossen",
    }

    const nextOccurrenceOrStartDate = useMemo(() => {
        if (!data.recurrenceRule) {
            return DateTime.fromISO(data.startDate).toLocaleString(DateTime.DATE_MED);
        }

        const nextOccurrence = RRule
            .fromString(data.recurrenceRule!)
            .after(DateTime.now().toJSDate(), true)

        if (!nextOccurrence) {
            console.error("No next occurrence found for recurrence rule: " + data.recurrenceRule);
            return null;
        }

        return DateTime.fromJSDate(nextOccurrence).toLocaleString(DateTime.DATE_MED);
    }, [data])

    return (
        <Box
            sx={(theme) => ({
                display: 'flex',
                borderBottom: `1px solid ${alpha(theme.palette.divider, .25)}`,
                padding: theme.spacing(1, 2),
                backgroundColor: data.type === Enum_Specialopeninghour_Type.Open
                    ? alpha(theme.palette.success.main, .25)
                    : alpha(theme.palette.error.main, .25),
            })}
        >
            {isDesktop
                ? (
                    /* Desktop Grid */
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            width: '100%',
                        }}
                    >
                        <Grid size={4}>
                            <Typography variant="body1">{nextOccurrenceOrStartDate}</Typography>
                        </Grid>
                        <Grid size="grow">
                            <Typography variant="body1">{data.title}</Typography>
                        </Grid>
                        <Grid size="auto" sx={{ textAlign: 'right' }}>
                            <Typography variant="body1" component="p" textTransform="uppercase">{typeTextMap[data.type]}</Typography>
                        </Grid>
                    </Grid>
                ) : (
                    /* Mobile Grid */
                    <Grid
                        container
                        spacing={2}
                        columns={2}
                        sx={{
                            width: '100%',
                        }}
                    >
                        <Grid size='auto'>
                            <Typography variant="body1">{nextOccurrenceOrStartDate}</Typography>
                        </Grid>
                        <Grid
                            size="grow"
                            sx={{
                                textAlign: 'right',
                            }}
                        >
                            <Typography variant="body1" component="p" textTransform="uppercase">{typeTextMap[data.type]}</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="body1">{data.title}</Typography>
                        </Grid>
                    </Grid>
                )
            }
        </Box>
    )
}

const sortByNextOccurrence = (a: SpecialOpeningHour, b: SpecialOpeningHour) => {
    const nextOccurrenceA = nextOccurrence(a);
    const nextOccurrenceB = nextOccurrence(b);

    return nextOccurrenceA && nextOccurrenceB
        ? DateTime.fromJSDate(nextOccurrenceA).diff(DateTime.fromJSDate(nextOccurrenceB)).milliseconds
        : 0;
}

const groupedByMonthAndYear = (acc: { [key: string]: SpecialOpeningHour[] }, event: SpecialOpeningHour) => {
    const startDate = DateTime.fromJSDate(nextOccurrence(event)!);
    const monthYearKey = `${startDate.month}-${startDate.year}`;

    if (!acc[monthYearKey]) {
        acc[monthYearKey] = [];
    }
    acc[monthYearKey].push(event);
    return acc;
}
